import React from 'react';
import { Box, Typography, type TypographyProps, type SxProps, type Theme } from '@mui/material';
import { DASHBOARD_BRAND_PRIMARY } from '../theme/dashboardBrand';

export function splitReferenceCode(code: string): { head: string; tail: string | null } {
  const trimmed = code.trim();
  const dashIndex = trimmed.lastIndexOf('-');
  if (dashIndex <= 0 || dashIndex >= trimmed.length - 1) {
    return { head: trimmed, tail: null };
  }
  return {
    head: trimmed.slice(0, dashIndex + 1),
    tail: trimmed.slice(dashIndex + 1),
  };
}

type FormattedReferenceCodeProps = {
  code: string;
  /** Préfixe libellé, ex. « Ref: » */
  labelPrefix?: string;
  /** Afficher # devant le code */
  hashPrefix?: boolean;
  /** Retirer le préfixe FO- (miroirs contact) */
  stripFoPrefix?: boolean;
  /** Mettre uniquement le suffixe après « - » dans le cadre bleu */
  boxed?: boolean;
  /** Afficher uniquement le numéro d'étiquette (suffixe après « - ») */
  suffixOnly?: boolean;
} & Pick<TypographyProps, 'variant' | 'color' | 'noWrap' | 'component'> & {
  sx?: SxProps<Theme>;
};

const getTailBadgeSx = (tail: string) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1.5px solid',
  borderColor: DASHBOARD_BRAND_PRIMARY,
  borderRadius: 1.25,
  px: 1.75,
  py: 0.35,
  minWidth: Math.max(48, tail.length * 18 + 14),
  minHeight: 28,
  bgcolor: 'background.paper',
  textAlign: 'center' as const,
  boxSizing: 'border-box' as const,
});

const FormattedReferenceCode: React.FC<FormattedReferenceCodeProps> = ({
  code,
  labelPrefix = '',
  hashPrefix = false,
  stripFoPrefix = false,
  boxed = true,
  suffixOnly = false,
  variant = 'inherit',
  color,
  sx,
  noWrap,
  component = 'span',
}) => {
  let normalized = code.trim();
  if (stripFoPrefix) {
    normalized = normalized.replace(/^FO-/, '');
  }
  const { head, tail } = splitReferenceCode(normalized);
  const prefix = hashPrefix ? '#' : '';

  if (suffixOnly && tail != null) {
    return (
      <Box component={component} sx={sx}>
        <Box component="span" sx={getTailBadgeSx(tail)}>
          <Typography
            component="span"
            sx={{
              fontWeight: 800,
              fontSize: '1rem',
              lineHeight: 1,
              color: 'text.primary',
              letterSpacing: 0.3,
            }}
          >
            {tail}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (!boxed || tail == null) {
    return (
      <Typography variant={variant} color={color} sx={sx} noWrap={noWrap} component={component}>
        {labelPrefix}
        {prefix}
        {head}
        {tail != null ? (
          <Box component="span" sx={{ fontWeight: 800, fontSize: '1.2em', lineHeight: 1 }}>
            {tail}
          </Box>
        ) : null}
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: 0.35,
        flexWrap: 'wrap',
        ...sx,
      }}
    >
      {labelPrefix ? (
        <Typography variant={variant} color={color ?? 'text.secondary'} component="span">
          {labelPrefix.trimEnd()}
        </Typography>
      ) : null}
      <Typography
        variant={variant}
        color={color ?? 'text.primary'}
        component="span"
        noWrap={noWrap}
        sx={{ fontWeight: 600, letterSpacing: 0.15, lineHeight: 1.2 }}
      >
        {prefix}
        {head}
      </Typography>
      <Box component="span" sx={getTailBadgeSx(tail)}>
        <Typography
          component="span"
          sx={{
            fontWeight: 800,
            fontSize: '1rem',
            lineHeight: 1,
            color: 'text.primary',
            letterSpacing: 0.3,
          }}
        >
          {tail}
        </Typography>
      </Box>
    </Box>
  );
};

export default FormattedReferenceCode;
